import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Eye, EyeOff, Lock, Mail, ArrowRight, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";

/**
 * Login — Zakelijke variant zonder foto.
 * Split-layout: links dark-navy brand-panel, rechts wit form-panel.
 * Enterprise SaaS-stijl (Stripe / Linear / Notion referentie).
 */
export default function Login() {
  const { login, completeTwoFa } = useAuth();
  const nav = useNavigate();
  const { search } = useLocation();
  const nextParam = new URLSearchParams(search).get("next");  // bij render vastleggen: na setUser vervangt de router de URL al
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null); // { token, setupRequired, name }

  useEffect(() => {
    document.title = "BPTplus · Inloggen";
  }, []);

  const naLogin = (data) => {
    try {
      sessionStorage.setItem("bpt_welkom", JSON.stringify({
        name: data.name,
        role: data.role,
        shownAt: Date.now(),
      }));
    } catch {}
    const revCode = sessionStorage.getItem("revolut_auth_code");
    const veiligNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
    nav(data.role === "onderaannemer" ? "/portaal" : (veiligNext || (revCode ? "/revolut" : "/dashboard")));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.twofa_required) {
        setTwofa({ token: data.twofa_token, setupRequired: data.setup_required, name: data.name });
        setLoading(false);
        return;
      }
      naLogin(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login mislukt");
    }
    setLoading(false);
  };

  const twoFaKlaar = async (data) => {
    await completeTwoFa(data);
    naLogin(data);
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-12" data-testid="login-page">
      {/* Links brand-panel */}
      <aside
        className="hidden lg:flex lg:col-span-5 xl:col-span-6 relative overflow-hidden text-white flex-col justify-between p-12 xl:p-16"
        style={{ background: "linear-gradient(160deg, #0b1120 0%, #111827 60%, #0f172a 100%)" }}
      >
        {/* Subtiele grid-lijnen als patroon */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
          aria-hidden
        />
        {/* Rood-accent diagonaal-band linksboven */}
        <div
          className="absolute -top-24 -left-32 h-64 w-96 rotate-[-16deg] opacity-25"
          style={{ background: "linear-gradient(90deg, hsl(var(--bpt-red)) 0%, transparent 100%)" }}
          aria-hidden
        />
        {/* Subtiel diagonaal-hoekje rechtsonder */}
        <div
          className="absolute -bottom-20 -right-20 h-72 w-72 opacity-10"
          style={{ background: "linear-gradient(135deg, transparent 0%, hsl(var(--bpt-red)) 100%)" }}
          aria-hidden
        />

        {/* Top: logo */}
        <div className="relative z-10">
          <img
            src="/bpt-logo.webp"
            alt="Brandpreventie Techniek"
            className="h-12 w-auto object-contain brightness-0 invert opacity-95"
            data-testid="bpt-logo"
          />
        </div>

        {/* Midden: brand statement */}
        <div className="relative z-10 space-y-6 max-w-md">
          <div className="flex items-center gap-2">
            <span className="inline-block h-[2px] w-8 bg-[hsl(var(--bpt-red))]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/60 font-semibold">
              BPTplus · ERP Portaal
            </span>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1.05]">
            Het besturings&shy;systeem<br />
            <span className="text-white/50">voor brand&shy;veiligheid.</span>
          </h1>
          <p className="text-white/60 text-sm leading-relaxed max-w-sm">
            Een geïntegreerd all-in-one bedrijfsmanagementplatform.
          </p>
        </div>

        {/* Onder: landen + certificaten */}
        <div className="relative z-10 space-y-6">
          <div className="flex items-center gap-6 border-t border-white/10 pt-6 max-w-md" data-testid="login-landen">
            <div className="flex items-center gap-2.5">
              <svg width="34" height="24" viewBox="0 0 9 6" className="rounded-[3px] shadow-md shrink-0" aria-label="Nederlandse vlag">
                <rect width="9" height="6" fill="#21468B" />
                <rect width="9" height="4" fill="#FFFFFF" />
                <rect width="9" height="2" fill="#AE1C28" />
              </svg>
              <span className="text-[10px] uppercase tracking-widest text-white/60">Nederland</span>
            </div>
            <div className="flex items-center gap-2.5">
              <svg width="34" height="24" viewBox="0 0 9 6" className="rounded-[3px] shadow-md shrink-0" aria-label="Belgische vlag">
                <rect width="3" height="6" fill="#000000" />
                <rect x="3" width="3" height="6" fill="#FDDA24" />
                <rect x="6" width="3" height="6" fill="#EF3340" />
              </svg>
              <span className="text-[10px] uppercase tracking-widest text-white/60">België</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="login-certificeringen">
            {[["REOB", "Deelgebied A1 — onderhoud blusmiddelen"], ["Apragaz"], ["VCA*"], ["ES-TRIN"], ["ISO 9001"], ["ISO 14001"]].map(([t, sub]) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border border-white/15 text-white/70 inline-flex flex-col leading-tight"
                data-testid={`login-cert-${t.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
              >
                <span>{t}</span>
                {sub && <span className="normal-case tracking-normal text-[9px] text-white/50">{sub}</span>}
              </span>
            ))}
          </div>
        </div>
      </aside>

      {/* Rechts form-panel */}
      <section className="col-span-1 lg:col-span-7 xl:col-span-6 bg-[#fafbfc] flex items-center justify-center p-6 sm:p-10 lg:p-14 relative">
        {/* Mobile brand top */}
        <div className="lg:hidden absolute top-6 left-6 flex items-center gap-2">
          <img src="/bpt-logo.webp" alt="BPTplus" className="h-8 w-auto object-contain" />
          <span className="text-xs font-bold tracking-widest text-[hsl(var(--bpt-red))]">BPTPLUS</span>
        </div>

        <div className="w-full max-w-md" data-testid="login-card">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--bpt-red))]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                Beveiligde toegang
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
              {twofa ? "Tweestapsverificatie" : "Welkom terug"}
            </h2>
            <p className="text-slate-500 text-sm mt-2 leading-relaxed">
              {twofa
                ? (twofa.setupRequired
                    ? "Stel eenmalig je authenticator-app in om verder te gaan."
                    : "Voer de 6-cijferige code uit je authenticator-app in.")
                : "Log in met uw bedrijfsaccount om verder te gaan."}
            </p>
          </div>

          {twofa ? (
            <TwoFaStap twofa={twofa} onKlaar={twoFaKlaar} onTerug={() => setTwofa(null)} />
          ) : (
          <form onSubmit={onSubmit} data-testid="login-form" className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
                E-mailadres
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="pl-9 h-11 bg-white border-slate-200 focus-visible:ring-1 focus-visible:ring-[hsl(var(--bpt-red))] focus-visible:border-[hsl(var(--bpt-red))]"
                  placeholder="uw.naam@bedrijf.nl"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
                Wachtwoord
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9 pr-10 h-11 bg-white border-slate-200 focus-visible:ring-1 focus-visible:ring-[hsl(var(--bpt-red))] focus-visible:border-[hsl(var(--bpt-red))]"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Verberg wachtwoord" : "Toon wachtwoord"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="login-toggle-pw"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="group w-full h-11 bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))] text-white font-semibold text-sm tracking-wide"
              data-testid="login-submit"
            >
              {loading ? "Bezig met inloggen…" : (
                <span className="inline-flex items-center gap-2">
                  Inloggen
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              )}
            </Button>

            <div className="pt-1 text-center">
              <Link
                to="/wachtwoord-vergeten"
                className="text-xs text-slate-500 hover:text-[hsl(var(--bpt-red))] transition-colors"
                data-testid="login-forgot-pw"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
          </form>
          )}

          <p className="text-xs text-slate-500 mt-8 leading-relaxed">
            Geen toegang? Neem contact op met uw manager of{" "}
            <a href="mailto:it@brandpreventietechniek.nl" className="text-[hsl(var(--bpt-red))] font-medium hover:underline">
              it-support
            </a>
            .
          </p>

          <div className="mt-10 pt-6 border-t border-slate-200">
            <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase">
              © {new Date().getFullYear()} · Brandpreventie Techniek B.V. · Alle rechten voorbehouden
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/** 2FA-stap: verificatie met code, of eerste setup (QR + backupcodes) */
function TwoFaStap({ twofa, onKlaar, onTerug }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [vertrouw, setVertrouw] = useState(false);
  const [setup, setSetup] = useState(null); // { qr_data_url, manual_key }
  const [backupCodes, setBackupCodes] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const setupStartedRef = React.useRef(false);

  useEffect(() => {
    if (twofa.setupRequired && !setupStartedRef.current) {
      setupStartedRef.current = true;
      api.post("/auth/2fa/setup-start", { twofa_token: twofa.token })
        .then(({ data }) => setSetup(data))
        .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "2FA-setup starten mislukt — log opnieuw in"));
    }
    // eslint-disable-next-line
  }, []);

  const bevestig = async (e) => {
    e.preventDefault();
    if (!code || busy) return;
    setBusy(true);
    try {
      if (twofa.setupRequired) {
        const { data } = await api.post("/auth/2fa/setup-confirm", { twofa_token: twofa.token, code, vertrouw_apparaat: vertrouw });
        if (data.trusted_device_token) localStorage.setItem("bpt_trusted_device", data.trusted_device_token);
        setBackupCodes(data.backup_codes || []);
        setPendingData(data);
      } else {
        const { data } = await api.post("/auth/2fa/verify", { twofa_token: twofa.token, code, vertrouw_apparaat: vertrouw });
        if (data.trusted_device_token) localStorage.setItem("bpt_trusted_device", data.trusted_device_token);
        await onKlaar(data);
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Ongeldige code");
    }
    setBusy(false);
  };

  if (backupCodes) {
    return (
      <div className="space-y-4" data-testid="twofa-backup-codes">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-800 mb-1">✓ 2FA staat aan</p>
          <p className="text-xs text-emerald-700">
            Bewaar deze backup-codes op een veilige plek (bv. wachtwoordmanager).
            Elke code werkt één keer als je je telefoon kwijt bent. Ze worden maar één keer getoond.
          </p>
        </div>
        <pre className="rounded-lg bg-slate-900 text-emerald-300 text-sm font-mono p-4 leading-7 select-all" data-testid="twofa-codes-lijst">
          {backupCodes.join("\n")}
        </pre>
        <Button
          onClick={() => onKlaar(pendingData)}
          className="w-full h-11 bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))] text-white font-semibold"
          data-testid="twofa-codes-opgeslagen"
        >
          Ik heb ze opgeslagen — doorgaan
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={bevestig} className="space-y-5" data-testid="twofa-form">
      {twofa.setupRequired && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong>Stap 1:</strong> installeer een authenticator-app (Google Authenticator, Microsoft Authenticator of Authy).<br />
            <strong>Stap 2:</strong> scan de QR-code hieronder.<br />
            <strong>Stap 3:</strong> voer de 6-cijferige code in.
          </p>
          {setup ? (
            <>
              <img src={setup.qr_data_url} alt="2FA QR-code" className="mx-auto h-44 w-44" data-testid="twofa-qr" />
              <p className="text-[11px] text-slate-500 text-center">
                Scannen lukt niet? Voer handmatig in:{" "}
                <code className="font-mono bg-slate-100 px-1 py-0.5 rounded select-all" data-testid="twofa-manual-key">{setup.manual_key}</code>
              </p>
            </>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">QR-code laden…</p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="twofa-code" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
          {twofa.setupRequired ? "Code uit de app" : "Verificatiecode"}
        </Label>
        <div className="relative mt-2">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            id="twofa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={twofa.setupRequired ? "123456" : "123456 of backup-code"}
            className="pl-9 h-11 bg-white border-slate-200 tracking-[0.3em] font-mono focus-visible:ring-1 focus-visible:ring-[hsl(var(--bpt-red))] focus-visible:border-[hsl(var(--bpt-red))]"
            data-testid="twofa-code-input"
          />
        </div>
        {!twofa.setupRequired && (
          <p className="text-[11px] text-slate-400 mt-1.5">Telefoon kwijt? Gebruik één van je backup-codes (XXXX-XXXX).</p>
        )}
      </div>

      <label className="flex items-center gap-2.5 cursor-pointer select-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
             data-testid="twofa-vertrouw-label">
        <input
          type="checkbox"
          checked={vertrouw}
          onChange={(e) => setVertrouw(e.target.checked)}
          className="h-4 w-4 accent-[hsl(var(--bpt-red))]"
          data-testid="twofa-vertrouw-checkbox"
        />
        <span className="text-xs text-slate-600">
          <span className="font-semibold text-slate-700">Vertrouw dit apparaat 30 dagen</span><br />
          Je hoeft op dit apparaat dan geen verificatiecode meer in te voeren.
        </span>
      </label>

      <Button
        type="submit"
        disabled={busy || !code}
        className="group w-full h-11 bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))] text-white font-semibold text-sm tracking-wide"
        data-testid="twofa-submit"
      >
        {busy ? "Controleren…" : (
          <span className="inline-flex items-center gap-2">
            {twofa.setupRequired ? "Activeren & inloggen" : "Verifiëren"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        )}
      </Button>

      <button type="button" onClick={onTerug}
              className="w-full text-xs text-slate-500 hover:text-slate-800 inline-flex items-center justify-center gap-1 transition-colors"
              data-testid="twofa-terug">
        <ArrowLeft className="h-3 w-3" /> Terug naar inloggen
      </button>
    </form>
  );
}
